import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import { PackageCheck, PlusCircle, Warehouse, ScanBarcode, Search, ArrowLeft, Forklift } from "lucide-react";
import { useTranslation } from "../i18n";
import ConveyorCompleteChutes from "../components/conveyor-complete-chutes";
import ConveyorCollectChute from "../components/conveyor-collect-chute";
import ConveyorAddPallet from "../components/conveyor-add-pallet";
import ConveyorHoldingPallets from "../components/conveyor-holding-pallets";
import ConveyorManualSorterScan from "../components/conveyor-manual-sorter-scan";
import ConveyorPalletValidator from "../components/conveyor-pallet-validator";
import ConveyorSearch from "../components/conveyor-search";
import { isVisible, useHomeModuleVisibility, readUserRole } from "../components/helpers/homeModuleVisibility";
import { useBadgeCounts } from "../hooks/useBadgeCounts";

import "../styles/conveyor.css";
import "../styles/scansheet.css";
import "../styles/home.css";

type CompleteChute = {
    axiomId: number;
    chute: string;
    palletId: string;
    system: string;
    timeStamp: string;
    tag: string;
    type: string;
    receivedItems: boolean;
    collected: boolean;
    processed: string;
};

const Conveyor = () => {
    usePageTitle("conveyor");
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [view, setView] = useState<string>("menu");
    const [selectedChute, setSelectedChute] = useState<CompleteChute | null>(null);
    const [chuteScanned, setChuteScanned] = useState<boolean>(false);

    const userData = window.localStorage.getItem("user");
    const userObject = userData !== null ? JSON.parse(userData) : { warehouse: "Newport" };
    const role = readUserRole();
    const moduleMap = useHomeModuleVisibility(userObject.warehouse);
    const show = (key: string) => isVisible(moduleMap, key, role);

    // Collect-chutes + missing-pallet badges come precomputed from the
    // server (GET /badges) — one poll for the whole menu instead of two
    // POSTs. The missing-pallet figure already excludes chutes 1 & 2
    // server-side, matching the old client-side filter.
    const counts = useBadgeCounts(userObject.warehouse || "Newport");
    const chuteCount = counts.conveyor.collect;
    const chutesMissingPalletsCount = counts.conveyor.addPallet;

    const goToMenu = () => {
        setView("menu");
        setSelectedChute(null);
    };

    const goToCompleteChutes = () => {
        setView("complete-chutes");
        setSelectedChute(null);
    };

    const handleSelectChute = (chute: CompleteChute, chuteScanned: boolean) => {
        setSelectedChute(chute);
        setChuteScanned(chuteScanned);
        setView("collect-chute");
    };

    // Menu view — wrapped in page-data for centered layout
    if (view === "menu") {
        return (
            <div className="page-data" style={{ height: "calc(100vh - 56px)", overflow: "auto" }}>
                <div className="page-title-bar">
                    <h1 className="page-title">{t("conveyor")}</h1>
                    <button className="page-back-btn" onClick={() => navigate("/")}>
                        <ArrowLeft size={20} />
                    </button>
                </div>
                <div className="home-grid">
                    {show("conveyor.collect") && (
                        <button className="home-card home-card-green" onClick={() => setView("complete-chutes")}>
                            {chuteCount > 0 && <span className="home-card-badge">{chuteCount}</span>}
                            <div className="home-card-icon"><PackageCheck size={48} strokeWidth={1.5} /></div>
                            <div className="home-card-title">{t("collect")}</div>
                        </button>
                    )}
                    {show("conveyor.add") && (
                        <button className="home-card home-card-green" onClick={() => setView("add-pallet")}>
                            {chutesMissingPalletsCount > 0 && <span className="home-card-badge">{chutesMissingPalletsCount}</span>}
                            <div className="home-card-icon"><PlusCircle size={48} strokeWidth={1.5} /></div>
                            <div className="home-card-title">{t("add")}</div>
                        </button>
                    )}
                    {show("conveyor.holding") && (
                        <button className="home-card home-card-green" onClick={() => setView("holding-pallets")}>
                            <div className="home-card-icon"><Warehouse size={48} strokeWidth={1.5} /></div>
                            <div className="home-card-title">{t("holding_pallets")}</div>
                        </button>
                    )}
                    {show("conveyor.manual_sort") && (
                        <button className="home-card home-card-green" onClick={() => setView("manual-sorter-scan")}>
                            <div className="home-card-icon"><ScanBarcode size={48} strokeWidth={1.5} /></div>
                            <div className="home-card-title">{t("manual_sorter_scan")}</div>
                        </button>
                    )}
                    {show("conveyor.pallet_validator") && (
                        <button className="home-card home-card-green" onClick={() => setView("pallet-validator")}>
                            <div className="home-card-icon"><Forklift size={48} strokeWidth={1.5} /></div>
                            <div className="home-card-title">{t("pallet_validator")}</div>
                        </button>
                    )}
                    {show("conveyor.search") && (
                        <button className="home-card home-card-grey-light" onClick={() => setView("search")}>
                            <div className="home-card-icon"><Search size={48} strokeWidth={1.5} /></div>
                            <div className="home-card-title">{t("search")}</div>
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // List view (complete chutes) — wrapped in page-data
    if (view === "complete-chutes") {
        return <ConveyorCompleteChutes onSelectChute={handleSelectChute} goBack={goToMenu} />;
    }

    // Scan sub-pages — render directly (no page-data wrapper) so scan-panel fills the screen
    if (view === "collect-chute" && selectedChute) {
        return <ConveyorCollectChute chute={selectedChute} chuteScanned={chuteScanned} goBack={goToCompleteChutes} />;
    }
    if (view === "add-pallet") {
        return <ConveyorAddPallet goBack={goToMenu} />;
    }
    if (view === "holding-pallets") {
        return <ConveyorHoldingPallets goBack={goToMenu} />;
    }
    if (view === "manual-sorter-scan") {
        return <ConveyorManualSorterScan goBack={goToMenu} />;
    }
    if (view === "pallet-validator") {
        return <ConveyorPalletValidator goBack={goToMenu} />;
    }
    if (view === "search") {
        return <ConveyorSearch goBack={goToMenu} />;
    }

    return null;
};

export default Conveyor;
